import React, { useState, useRef, useEffect } from "react";
import { View, TextInput } from "react-native-web";
import { useNavigate } from "react-router-dom";
import CustomText from "../components/atoms/CustomText";
import Button from "../components/common/Button";
import { colors, spacing, typography } from "../utils/styling";
import { useResponsive } from "../hooks/useResponsive";
import { useForgotPassword } from "../lib/auth";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { mutateAsync: forgotPassword } = useForgotPassword();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const { isMobile } = useResponsive();
  const mobileLogoSize = 108;

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const handleSubmit = async () => {
    setError("");
    setSuccess(false);

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      await forgotPassword({ email });
      setSuccess(true);
    } catch (err) {
      let errorMessage = "Failed to send reset email";
      
      if (err instanceof Error) {
        const message = err.message;
        if (message.includes("User not found")) {
          errorMessage = "No account found with this email address";
        } else if (message.includes("Unable to verify")) {
          errorMessage = "Unable to verify your account. Please try again later.";
        } else if (message.includes("Failed to send")) {
          errorMessage = "Unable to send reset email. Please try again later.";
        } else if (message.includes("Too many password reset requests")) {
          errorMessage = "Too many reset attempts. Please wait a minute before trying again.";
        } else {
          errorMessage = message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View
      style={{
        flex: 1,
        flexDirection: isMobile ? "column" : "row",
        marginTop: isMobile ? 12.5 : undefined,
      }}
    >
      {/* Left Side - Form */}
      <View
        style={{
          flex: isMobile ? undefined : 0.6,
          width: isMobile ? "100%" : undefined,
          backgroundColor: colors.gray[100],
          padding: isMobile ? spacing.lg : spacing.xxl,
          alignItems: "center",
          justifyContent: "center",
          boxShadow: isMobile
            ? undefined
            : "inset -1px 0 0 0 rgba(0, 0, 0, 0.05), inset -2px 0 0 0 rgba(0, 0, 0, 0.03)",
        }}
      >
        {/* Logo on mobile */}
        {isMobile && (
          <View style={{ marginBottom: spacing.xl, alignSelf: "center" }}>
            <img
              src="/transparent-logo.png"
              alt="Money Personality Logo"
              style={{
                height: 108,
                width: "auto",
                cursor: "pointer",
                objectFit: "contain",
                padding: 24,
              }}
              onClick={() => navigate("/")}
            />
          </View>
        )}

        {/* Form Content */}
        <View style={{ width: "100%", maxWidth: isMobile ? 340 : 600 }}>
          <CustomText
            style={{
              fontSize: isMobile ? 30 : 60,
              fontWeight: "400",
              marginBottom: isMobile ? spacing.xs : spacing.sm,
              fontFamily: "Nunito",
              textAlign: isMobile ? "center" : undefined,
            }}
          >
            Reset Password
          </CustomText>

          <CustomText
            style={{
              fontSize: isMobile ? 18 : 24,
              color: colors.gray[600],
              marginBottom: isMobile ? spacing.md : spacing.lg,
              fontFamily: "Nunito",
              textAlign: isMobile ? "center" : undefined,
            }}
          >
            Enter your email address and we'll send you a link to reset your
            password
          </CustomText>

          {!success ? (
            <>
              <View style={{ marginBottom: spacing.xl }}>
                <View style={{ marginBottom: spacing.md }}>
                  <CustomText
                    style={{
                      marginBottom: spacing.sm,
                      color: colors.dark,
                      fontFamily: "Nunito",
                      fontSize: 12,
                    }}
                  >
                    Email
                  </CustomText>
                  <TextInput
                    ref={emailInputRef}
                    value={email}
                    onChangeText={setEmail}
                    placeholder="john@example.com"
                    placeholderTextColor={colors.gray[500]}
                    style={{
                      height: 40,
                      borderWidth: 1,
                      borderColor: colors.gray[200],
                      borderRadius: 6,
                      paddingHorizontal: 12,
                      outlineStyle: "none",
                      width: "100%",
                      fontFamily: "Nunito",
                      fontSize: 14,
                      backgroundColor: colors.white,
                    }}
                  />
                </View>
              </View>

              {error && (
                <CustomText
                  style={{
                    color: colors.danger,
                    marginTop: spacing.sm,
                    fontSize: isMobile ? 14 : 18,
                    fontFamily: "Nunito",
                    marginBottom: 10,
                    paddingLeft: 4,
                  }}
                >
                  {error}
                </CustomText>
              )}

              <View style={{ marginBottom: spacing.xl }}>
                <Button
                  onPress={handleSubmit}
                  label="Send Reset Link"
                  loading={isSubmitting}
                  disabled={!email || isSubmitting}
                  rounded={false}
                  style={{
                    width: "100%",
                    height: 40,
                  }}
                  textStyle={{
                    fontSize: 14,
                    fontFamily: "Nunito",
                  }}
                />
              </View>

              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CustomText
                  style={{
                    fontSize: 14,
                    color: colors.gray[600],
                    fontFamily: "Nunito",
                  }}
                >
                  Remember your password?{" "}
                </CustomText>
                <Button
                  onPress={() => navigate("/login")}
                  label="Back to Login"
                  style={{
                    backgroundColor: "transparent",
                    paddingHorizontal: 0,
                    paddingVertical: 0,
                  }}
                  textStyle={{
                    fontSize: 14,
                    color: colors.primary,
                    textDecorationLine: "underline",
                    fontFamily: "Nunito",
                  }}
                />
              </View>
            </>
          ) : (
            <>
              <View
                style={{
                  backgroundColor: colors.green[50],
                  borderColor: colors.green[200],
                  borderWidth: 1,
                  borderRadius: 6,
                  padding: spacing.md,
                  marginBottom: spacing.xl,
                }}
              >
                <CustomText
                  style={{
                    color: colors.green[800],
                    fontSize: 14,
                    fontFamily: "Nunito",
                    textAlign: "center",
                  }}
                >
                  Reset link sent! Check your email for instructions to reset
                  your password.
                </CustomText>
              </View>

              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CustomText
                  style={{
                    fontSize: 14,
                    color: colors.gray[600],
                    fontFamily: "Nunito",
                  }}
                >
                  Didn't receive the email?{" "}
                </CustomText>
                <Button
                  onPress={() => setSuccess(false)}
                  label="Try Again"
                  style={{
                    backgroundColor: "transparent",
                    paddingHorizontal: 0,
                    paddingVertical: 0,
                  }}
                  textStyle={{
                    fontSize: 14,
                    color: colors.primary,
                    textDecorationLine: "underline",
                  }}
                />
              </View>
            </>
          )}
        </View>
      </View>

      {/* Right Side - Testimonial */}
      <View
        style={{
          flex: isMobile ? undefined : 0.4,
          width: isMobile ? "100%" : undefined,
          backgroundColor: colors.white,
          padding: isMobile ? spacing.lg : spacing.xxl,
          alignItems: isMobile ? "center" : undefined,
          height: isMobile ? "100%" : undefined,
        }}
      >
        {/* Logo only on desktop */}
        {!isMobile && (
          <View style={{ marginBottom: spacing.xxl, alignSelf: "flex-end" }}>
            <img
              src="/transparent-logo.png"
              alt="Money Personality Logo"
              style={{
                height: 60,
                width: "auto",
                cursor: "pointer",
                objectFit: "contain",
              }}
              onClick={() => navigate("/")}
            />
          </View>
        )}

        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: isMobile ? spacing.lg : 100,
          }}
        >
          <View style={{ maxWidth: 420 }}>
            <CustomText
              style={{
                fontSize: 28,
                lineHeight: 42,
                color: colors.gray[900],
                marginBottom: spacing.xl,
                fontFamily: "Nunito",
                textAlign: isMobile ? "center" : undefined,
              }}
            >
              "I can't believe how accurate the Money Personality test is...it
              felt like someone had been watching my relationship with money my
              entire life."
            </CustomText>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: isMobile ? "center" : undefined,
              }}
            >
              <img
                src={require("../components/img/ben-pic.jpeg")}
                alt="Ben's profile"
                style={{
                  width: isMobile ? 75 : 100,
                  height: "auto",
                  borderRadius: 100,
                  objectFit: "cover",
                  marginRight: spacing.md,
                }}
              />
              <CustomText
                style={{
                  fontSize: 18,
                  color: colors.gray[600],
                  fontFamily: "Nunito"
                }}
              >
                Ben Barton, Entrepreneur
              </CustomText>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
